/** Тексты анализа для каждой пары знаков (12×12). */

type ZodiacPairCopy = {
  positive: string;
  negative: string;
  tip: string;
};

const ZODIAC_SIGNS = [
  "Овен",
  "Телец",
  "Близнецы",
  "Рак",
  "Лев",
  "Дева",
  "Весы",
  "Скорпион",
  "Стрелец",
  "Козерог",
  "Водолей",
  "Рыбы",
] as const;

type ZodiacSignName = (typeof ZODIAC_SIGNS)[number];

type SignProfile = {
  element: string;
  gift: string;
  need: string;
  friction: string;
  inLove: string;
};

const SIGN_PROFILE: Record<ZodiacSignName, SignProfile> = {
  Овен: {
    element: "огонь",
    gift: "прямую энергию, инициативу и смелость",
    need: "уважение к своему темпу и свободе действий",
    friction: "нетерпение и желание всё решить сразу",
    inLove: "влюбляется быстро и ведёт за собой",
  },
  Телец: {
    element: "земля",
    gift: "надёжность, тепло и умение создавать уют",
    need: "предсказуемость и ощущение безопасности",
    friction: "упрямство и нежелание менять привычный ритм",
    inLove: "строит отношения медленно, но основательно",
  },
  Близнецы: {
    element: "воздух",
    gift: "лёгкость, остроумие и живой интерес к жизни",
    need: "интеллектуальный контакт и пространство для общения",
    friction: "поверхностность и скачки настроения",
    inLove: "цепляет разговором и новизной",
  },
  Рак: {
    element: "вода",
    gift: "заботу, эмпатию и глубокую привязанность",
    need: "эмоциональную опору и ощущение «своего дома»",
    friction: "обидчивость и уход в себя",
    inLove: "раскрывается, когда чувствует доверие",
  },
  Лев: {
    element: "огонь",
    gift: "щедрость, харизму и верность",
    need: "признание и искреннее восхищение",
    friction: "гордость и потребность быть в центре",
    inLove: "дарит яркость и романтику",
  },
  Дева: {
    element: "земля",
    gift: "внимание к деталям, практичность и заботу в делах",
    need: "порядок, ясность и уважение к границам",
    friction: "критичность и тревожность",
    inLove: "показывает чувства через поступки",
  },
  Весы: {
    element: "воздух",
    gift: "дипломатичность, вкус и стремление к гармонии",
    need: "партнёрство без грубого давления",
    friction: "нерешительность и уход от конфликта",
    inLove: "ищет красоту и равновесие в паре",
  },
  Скорпион: {
    element: "вода",
    gift: "глубину, страсть и преданность",
    need: "честность и эмоциональную вовлечённость",
    friction: "ревность и потребность всё контролировать",
    inLove: "любит всерьёз и надолго",
  },
  Стрелец: {
    element: "огонь",
    gift: "оптимизм, юмор и тягу к развитию",
    need: "свободу и общие горизонты",
    friction: "прямолинейность и неусидчивость",
    inLove: "зажигает приключением и честным разговором",
  },
  Козерог: {
    element: "земля",
    gift: "ответственность, выдержку и целеустремлённость",
    need: "уважение к целям и серьёзность намерений",
    friction: "сдержанность и работу важнее эмоций",
    inLove: "проверяет времяем и делами",
  },
  Водолей: {
    element: "воздух",
    gift: "оригинальность, независимость и широкий взгляд",
    need: "личное пространство и принятие своей инаковости",
    friction: "отстранённость и непредсказуемость",
    inLove: "притягивает дружбой и общими идеями",
  },
  Рыбы: {
    element: "вода",
    gift: "чувствительность, фантазию и мягкость",
    need: "нежность и эмоциональную близость",
    friction: "растворение в партнёре и уход от реальности",
    inLove: "влюбляется душой и ищет слияния",
  },
};

const ZODIAC_COMPAT: Record<ZodiacSignName, ZodiacSignName[]> = {
  Овен: ["Лев", "Стрелец", "Близнецы", "Водолей"],
  Телец: ["Дева", "Козерог", "Рак", "Рыбы"],
  Близнецы: ["Весы", "Водолей", "Овен", "Лев"],
  Рак: ["Скорпион", "Рыбы", "Телец", "Дева"],
  Лев: ["Овен", "Стрелец", "Близнецы", "Весы"],
  Дева: ["Телец", "Козерог", "Рак", "Скорпион"],
  Весы: ["Близнецы", "Водолей", "Лев", "Стрелец"],
  Скорпион: ["Рак", "Рыбы", "Дева", "Козерог"],
  Стрелец: ["Овен", "Лев", "Весы", "Водолей"],
  Козерог: ["Телец", "Дева", "Скорпион", "Рыбы"],
  Водолей: ["Близнецы", "Весы", "Овен", "Стрелец"],
  Рыбы: ["Рак", "Скорпион", "Телец", "Козерог"],
};

/** Курированные тексты для каждой пары (ключ «Овен|Телец», порядок важен). */
const PAIR_OVERRIDES: Record<string, ZodiacPairCopy> = {};

function pairKey(a: ZodiacSignName, b: ZodiacSignName): string {
  return `${a}|${b}`;
}

function elementHarmony(el1: string, el2: string): "harmony" | "neutral" | "tension" {
  const pairs: Record<string, "harmony" | "neutral" | "tension"> = {
    "огонь|огонь": "harmony",
    "огонь|воздух": "harmony",
    "воздух|огонь": "harmony",
    "земля|земля": "harmony",
    "земля|вода": "harmony",
    "вода|земля": "harmony",
    "вода|вода": "harmony",
    "воздух|воздух": "harmony",
    "огонь|земля": "tension",
    "земля|огонь": "tension",
    "огонь|вода": "tension",
    "вода|огонь": "tension",
    "воздух|земля": "neutral",
    "земля|воздух": "neutral",
    "воздух|вода": "neutral",
    "вода|воздух": "neutral",
  };
  return pairs[`${el1}|${el2}`] ?? "neutral";
}

function buildPairCopy(self: ZodiacSignName, peer: ZodiacSignName): ZodiacPairCopy {
  const override = PAIR_OVERRIDES[pairKey(self, peer)];
  if (override) return override;

  const a = SIGN_PROFILE[self];
  const b = SIGN_PROFILE[peer];
  const harmonious = ZODIAC_COMPAT[self].includes(peer);
  const sameSign = self === peer;
  const elements = elementHarmony(a.element, b.element);

  let positive: string;
  let negative: string;
  let tip: string;

  if (sameSign) {
    positive = `Вы оба — ${self}: легко понимаете мотивы друг друга. ${a.gift} усиливается вдвойне, а ${a.inLove} создаёт узнаваемый ритм пары.`;
    negative = `Два ${self} могут спорить из‑за одинаковых слабых мест: ${a.friction}. В паре не хватает «другого взгляда».`;
    tip = `Делите роли: один инициирует, другой поддерживает — так сильные стороны знака не превращаются в конкуренцию.`;
  } else if (harmonious) {
    positive = `${self} даёт ${a.gift}, а ${peer} — ${b.gift}. Классическая астрология считает эту пару удачной: ${a.inLove} хорошо сочетается с тем, как ${peer} строит близость. Стихии ${a.element} и ${b.element} обычно подкрепляют друг друга.`;
    negative = `Даже в удачной паре есть трение: ${a.friction} у ${self} может раздражать ${peer}, а ${b.friction} — наоборот.`;
    tip = `Опирайтесь на сильные стороны знаков и заранее договаривайтесь о темпе — ${a.need} и ${b.need} важны обоим.`;
  } else if (elements === "tension") {
    positive = `${self} (${a.element}) и ${peer} (${b.element}) тянут в разные стороны, но это даёт рост: вы дополняете друг друга там, где один импульсивен, другой — основателен.`;
    negative = `Стихии ${a.element} и ${b.element} часто спорят: ${a.friction} сталкивается с ${b.friction}. Без диалога легко почувствовать, что вас не слышат.`;
    tip = `Не пытайтесь переделать партнёра — лучше назвать вслух, что вам нужно: ${a.need} и ${b.need}.`;
  } else {
    positive = `${self} приносит ${a.gift}, ${peer} — ${b.gift}. Есть точки соприкосновения, если уважать разный ритм: ${a.inLove} и стиль ${peer}.`;
    negative = `Знаки не из «классического» списка совместимости — возможны недопонимания. ${a.friction} и ${b.friction} усиливаются, если не проговаривать ожидания.`;
    tip = `Ищите общий язык через конкретные договорённости, а не через догадки — особенно в быту и в теме «сколько времени вместе / порознь».`;
  }

  return { positive, negative, tip };
}

/** Матрица 12×12 — заполняется при загрузке модуля. */
const ZODIAC_PAIR_TEXTS: Record<ZodiacSignName, Record<ZodiacSignName, ZodiacPairCopy>> = {} as Record<
  ZodiacSignName,
  Record<ZodiacSignName, ZodiacPairCopy>
>;

for (const self of ZODIAC_SIGNS) {
  ZODIAC_PAIR_TEXTS[self] = {} as Record<ZodiacSignName, ZodiacPairCopy>;
  for (const peer of ZODIAC_SIGNS) {
    ZODIAC_PAIR_TEXTS[self][peer] = buildPairCopy(self, peer);
  }
}

/** Дополнительные уникальные формулировки для частых и контрастных пар. */
function setPair(self: ZodiacSignName, peer: ZodiacSignName, copy: ZodiacPairCopy): void {
  PAIR_OVERRIDES[pairKey(self, peer)] = copy;
  ZODIAC_PAIR_TEXTS[self][peer] = copy;
}

setPair("Овен", "Лев", {
  positive: "Овен зажигает искру, Лев умеет её удержать. Вместе вы энергичны, щедры и не боитесь проявлять чувства открыто.",
  negative: "Оба любите лидировать — споры «кто главный» возможны, если не делить сцену.",
  tip: "Чередуйте инициативу: сегодня ведёт один, завтра — другой.",
});
setPair("Овен", "Телец", {
  positive: "Овен приносит движение, Телец — стабильность. Телец заземляет импульс Овна, Овен выводит Тельца из зоны комфорта.",
  negative: "Овен торопит, Телец тормозит. Без терпения каждый считает другого «не тем».",
  tip: "Договоритесь о темпе: быстрые решения — в мелочах, крупные — без спешки.",
});
setPair("Овен", "Рак", {
  positive: "Овен защищает и действует, Рак создаёт эмоциональный дом. Есть потенциал для яркой и тёплой пары.",
  negative: "Прямота Овна ранит чувствительного Рака; обиды Рака кажутся Овну непонятными.",
  tip: "Овену — мягче в словах, Раку — говорить о чувствах сразу, не копить.",
});
setPair("Овен", "Весы", {
  positive: "Овен — решительность, Весы — такт. Весы сглаживают острые углы, Овен помогает Весам не зависать в выборе.",
  negative: "Овену кажется, что Весы тянут время; Весам — что Овен давит.",
  tip: "Фиксируйте решения дедлайном: обсуждаем до пятницы, в пятницу выбираем.",
});
setPair("Телец", "Скорпион", {
  positive: "Классическая «магнитная» пара земли и воды: глубокая привязанность, верность, сильная физическая химия.",
  negative: "Оба упрямы и ревнивы. Скрытность Скорпиона и молчание Тельца могут копить напряжение.",
  tip: "Раз в неделю — честный разговор без телефонов: что радует, что тревожит.",
});
setPair("Близнецы", "Стрелец", {
  positive: "Огонь и воздух: много смеха, путешествий, идей. Вам не скучно — вы расширяете кругозор друг друга.",
  negative: "Оба легко отвлекаются. Обещания могут остаться на уровне планов.",
  tip: "Одно общее дело с конкретной датой в календаре сильнее десяти абстрактных «когда‑нибудь».",
});
setPair("Рак", "Рыбы", {
  positive: "Два водных знака: тонкая эмпатия, романтика, умение чувствовать без слов.",
  negative: "Эмоции накрывают обоих — легко уйти в обиды или иллюзии вместо ясного разговора.",
  tip: "Наземляйте чувства фактами: «мне неприятно, когда…» вместо намёков.",
});
setPair("Лев", "Водолей", {
  positive: "Притяжение противоположностей: Лев — сердце, Водолей — идея. Вместе ярко и необычно.",
  negative: "Льву нужно восхищение, Водолею — дистанция. Формат близости может не совпасть.",
  tip: "Обсудите, как каждый понимает «достаточно внимания» — цифрами и примерами.",
});
setPair("Дева", "Рыбы", {
  positive: "Дева помогает Рыбам с бытом и ясностью, Рыбы смягчают критичность Девы и добавляют тепла.",
  negative: "Деве кажется, что Рыбы «витают»; Рыбам — что их постоянно поправляют.",
  tip: "Договор: Дева не чинит без запроса, Рыбы озвучивают просьбу о помощи вслух.",
});
setPair("Весы", "Козерог", {
  positive: "Весы приносят лёгкость и социальность, Козерог — опору и планы. Хорошая пара «мечта + реализация».",
  negative: "Козерог серьёзен, Весы избегают тяжёлых тем. Разный ритм отдыха и работы.",
  tip: "Совместный календарь: и свидания, и «тихие вечера» — оба варианта в графике.",
});
setPair("Скорпион", "Стрелец", {
  positive: "Страсть Скорпиона и свобода Стрельца дают интенсивные, живые отношения.",
  negative: "Скорпион хочет глубины и контроля, Стрелец — простора. Ревность vs независимость.",
  tip: "Правило прозрачности: где вы друг для друга, а где — свои друзья и хобби.",
});
setPair("Козерог", "Водолей", {
  positive: "Козерог структурирует, Водолей приносит свежие идеи. Вместе можно строить нестандартное, но устойчивое.",
  negative: "Козерогу нужны правила, Водолею — свобода ломать шаблон. Споры о «как правильно».",
  tip: "Разделите зоны: в быту — договорённости, в творчестве — эксперимент без оценки.",
});

export function getZodiacPairCopy(selfSign: string, peerSign: string): ZodiacPairCopy | null {
  if (!(ZODIAC_SIGNS as readonly string[]).includes(selfSign)) return null;
  if (!(ZODIAC_SIGNS as readonly string[]).includes(peerSign)) return null;
  return ZODIAC_PAIR_TEXTS[selfSign as ZodiacSignName][peerSign as ZodiacSignName];
}
